import { createFilterOptions, Stack } from '@mui/material'

import { C1Button } from 'c1ui'
import { Filter } from 'components/core/search-filter/filter'
import { QueryFilter } from 'components/core/search-filter/query'
import { useSearch, MetaFields } from 'lib/search'
import { AppUserServiceSearchRequest, AppUserStatus_Status, AppUserType, AppUserDomain } from 'pbts/c1/api/app/v1/app_user'
import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AppUserOwnerFilter } from './AppUserOwnerFilter'

export type AppUserFilterKeys = keyof Omit<AppUserServiceSearchRequest, MetaFields>

type AppUserFilterUpdate = Partial<Pick<AppUserServiceSearchRequest, AppUserFilterKeys>>

type AppUserFilterProps = {
    filter: AppUserServiceSearchRequest
    setFilter: (f: (filter: AppUserServiceSearchRequest) => AppUserServiceSearchRequest) => void
    displayFilters: AppUserFilterKeys[]
    emptyFilter?: Partial<AppUserServiceSearchRequest>
}

export const AppUserFilters = ({ filter, setFilter, displayFilters, emptyFilter = {} }: AppUserFilterProps): React.ReactElement => {
    const { canClear, clear, onChange, onQueryChange } = useSearch(
        AppUserServiceSearchRequest,
        filter,
        setFilter,
        emptyFilter,
    )

    const shouldShowFilter = useCallback(
        (filterKey: AppUserFilterKeys): boolean => {
            return displayFilters.includes(filterKey)
        },
        [displayFilters],
    )

    return <Stack direction="row" spacing={2} alignItems="flex-end">
        {shouldShowFilter('query') && (
            <QueryFilter
                autoFocus
                query={filter.query}
                setQuery={(q): void => onQueryChange(q as unknown as string, true)}
                onChange={onQueryChange}
            />
        )}
        {shouldShowFilter('appUserStatuses') && (
            <AppUserStatusFilter value={filter.appUserStatuses} onChange={onChange} />
        )}
        {shouldShowFilter('appUserTypes') && (
            <AppUserTypeFilter value={filter.appUserTypes} onChange={onChange} />
        )}
        {shouldShowFilter('appUserDomains') && (
            <AppUserDomainFilter value={filter.appUserDomains} onChange={onChange} />
        )}
        {shouldShowFilter('userIds') && (
            <AppUserOwnerFilter filter={filter} onChange={onChange} />
        )}
        {canClear && (
            <C1Button onClick={clear} variant="text" color="info">
                <FormattedMessage id="components_app_user_search_index_clear" defaultMessage="Clear" />
            </C1Button>
        )}
    </Stack>
}

type AppUserStatusFilterProps = {
    value?: Array<AppUserStatus_Status>
    onChange: (updated: AppUserFilterUpdate, shouldPropagate?: boolean) => void
}

const AppUserStatusFilterOptions = [AppUserStatus_Status.ENABLED, AppUserStatus_Status.DISABLED, AppUserStatus_Status.DELETED]
const StatusFilterOptions = createFilterOptions<AppUserStatus_Status>({})
const AppUserStatusFilter = ({ value, onChange: _onChange }: AppUserStatusFilterProps): React.ReactElement => {
    const intl = useIntl()

    const renderLabel = useCallback(
        (option: AppUserStatus_Status): string =>
            ({
                [AppUserStatus_Status.ENABLED]: intl.formatMessage({ id: 'enabled', defaultMessage: 'Active' }),
                [AppUserStatus_Status.DISABLED]: intl.formatMessage({ id: 'disabled', defaultMessage: 'Suspended' }),
                [AppUserStatus_Status.DELETED]: intl.formatMessage({ id: 'deleted', defaultMessage: 'Deleted' }),
            }[option]),
        [intl],
    )

    const onChange = useCallback(
        (appUserStatuses: Array<AppUserStatus_Status>): void => _onChange({ appUserStatuses }),
        [_onChange],
    )

    return (
        <Filter<AppUserStatus_Status>
            label={intl.formatMessage({
                id: 'app_user_filter_status_type',
                defaultMessage: 'Status',
            })}
            options={AppUserStatusFilterOptions}
            selected={value}
            onChange={onChange}
            filterOptions={StatusFilterOptions}
            renderLabel={renderLabel}
        />
    )
}

type AppUserTypeFilterProps = {
    value?: Array<AppUserType>
    onChange: (updated: AppUserFilterUpdate, shouldPropagate?: boolean) => void
}

const AppUserTypeFilterOptions = [AppUserType.USER, AppUserType.SERVICE_ACCOUNT]
const TypeFilterOptions = createFilterOptions<AppUserType>({})
const AppUserTypeFilter = ({ value, onChange: _onChange }: AppUserTypeFilterProps): React.ReactElement => {
    const intl = useIntl()

    const renderLabel = useCallback(
        (option: AppUserType): string =>
            ({
                [AppUserType.USER]: intl.formatMessage({ id: 'human', defaultMessage: 'Human' }),
                [AppUserType.SERVICE_ACCOUNT]: intl.formatMessage({ id: 'service', defaultMessage: 'Service' }),
            }[option]),
        [intl],
    )

    const onChange = useCallback(
        (appUserTypes: Array<AppUserType>): void => _onChange({ appUserTypes }),
        [_onChange],
    )

    return (
        <Filter<AppUserType>
            label={intl.formatMessage({
                id: 'app_user_filter_type',
                defaultMessage: 'Type',
            })}
            options={AppUserTypeFilterOptions}
            selected={value}
            onChange={onChange}
            filterOptions={TypeFilterOptions}
            renderLabel={renderLabel}
        />
    )
}

type AppUserDomainFilterProps = {
    value?: Array<AppUserDomain>
    onChange: (updated: AppUserFilterUpdate, shouldPropagate?: boolean) => void
}

const DomainFilterOptions = createFilterOptions<AppUserDomain>({})
const AppUserDomainFilter = ({ value, onChange: _onChange }: AppUserDomainFilterProps): React.ReactElement => {
    const intl = useIntl()

    // Available domain options from the enum
    const availableDomains: AppUserDomain[] = [AppUserDomain.EXTERNAL, AppUserDomain.TRUSTED]

    const onChange = useCallback(
        (appUserDomains: Array<AppUserDomain>): void => _onChange({ appUserDomains }),
        [_onChange],
    )

    return (
        <Filter<AppUserDomain>
            label={intl.formatMessage({
                id: 'app_user_filter_domain',
                defaultMessage: 'Domain',
            })}
            options={availableDomains}
            selected={value}
            onChange={onChange}
            filterOptions={DomainFilterOptions}
            renderLabel={(domain): string => {
                switch (domain) {
                    case AppUserDomain.EXTERNAL:
                        return intl.formatMessage({ id: 'external', defaultMessage: 'External' })
                    case AppUserDomain.TRUSTED:
                        return intl.formatMessage({ id: 'trusted', defaultMessage: 'Trusted' })
                    default:
                        return 'Unknown'
                }
            }}
        />
    )
}
